import { useEffect, useRef, useState } from 'react';
import { FlowEditorMode, USER_MODES } from 'src/features/flows/contexts/FlowContext';
import { useOrg } from 'src/shared/contexts/OrgContext';
import useQueryParams from 'src/shared/hooks/useQueryParams';
import { useSocket } from 'src/shared/hooks/useSocket';
import mixpanel from 'src/shared/lib/mixpanel';
import { Flow } from 'src/features/flows/types/flow.types';

export interface FlowLock {
  lock: 'LOCKED' | 'AVAILABLE' | 'ACTIVE';
  author?: {
    id: string;
    email: string;
    name: string;
  };
}

export default function useFlowLock(
  activeFlow: Flow | undefined,
  flowEditMode: FlowEditorMode,
  setFlowEditMode: (newEditMode: FlowEditorMode) => Promise<FlowEditorMode>
) {
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const { role } = useOrg();
  const { param: modeParam } = useQueryParams('mode');
  const [waitingForFirstLockInfo, setWaitingForFirstLockInfo] = useState(true);

  const allowedModes = role === 'READER' ? USER_MODES.filter((mode) => mode !== 'EDIT') : USER_MODES;

  const lockSocket = useSocket<FlowLock>({
    event: 'lock-info',
    roomId: [`flow`, activeFlow?.id || ''],
    disabled: !activeFlow?.id,
  });

  // 1. on initializing the socket, send the lock info event to get the initial lock state
  // this will trigger the hook below
  useEffect(() => {
    if (lockSocket.socketReady) {
      lockSocket.emit('lock-info');
      timeoutRef.current = setTimeout(() => {
        console.warn('Flow lock info socket timed out');
        setWaitingForFirstLockInfo(false);
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockSocket.socketReady]);

  // 2. Listen to lock state changes and switch to the correct mode if needed
  useEffect(() => {
    if (!lockSocket.data?.lock && waitingForFirstLockInfo) return;

    if (['INITIALIZING'].includes(flowEditMode)) {
      // if the waiting period is resolved before we had the first lock info event,
      // we assume the flow is locked, otherwise we use the lock info event
      let isLocked = !waitingForFirstLockInfo || lockSocket.data?.lock === 'LOCKED';
      handleSetInitialMode(isLocked);
    }

    // if the flow gets locked while we are in edit mode, switch to read mode
    // this should in theory never happen because you cannot loose your edit lock
    else if (lockSocket.data?.lock === 'LOCKED' && flowEditMode === 'EDIT') {
      setFlowEditMode('READ');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockSocket.data?.lock, waitingForFirstLockInfo]);

  // 3. Lock the flow every 30 seconds if we are in edit mode
  useEffect(() => {
    if (flowEditMode !== 'EDIT') return;

    const interval = setInterval(() => {
      lockSocket.emit('lock');
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowEditMode]);

  async function handleSetInitialMode(isLockedBySomeoneElse: boolean) {
    if (isLockedBySomeoneElse) {
      mixpanel.track('User Got Flow Locked');
    }
    clearTimeout(timeoutRef.current);
    const defaultMode = isLockedBySomeoneElse || role === 'READER' ? 'READ' : 'EDIT';
    await setFlowEditMode(
      modeParam && allowedModes.filter((mode) => mode !== 'REVIEW').includes(modeParam)
        ? (modeParam as FlowEditorMode)
        : defaultMode
    );
    setWaitingForFirstLockInfo(false);
  }

  return lockSocket;
}
